import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Hotel, Instagram, Waves, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Carousel, CarouselContent, CarouselItem } from "../components/ui/carousel";
import { ScrollVideoStage } from "../components/ScrollVideoStage";
import { activities, contact, media, stays } from "../data/siteData";
import { useSEO } from "../hooks/useSEO";

const FeatureCard = ({ icon: Icon, title, text, testId, to }) => {
  const content = (
    <>
      <Icon className="h-8 w-8 text-[#FFC059] transition-transform duration-300 group-hover:scale-110" />
      <h3 data-testid={`${testId}-title`} className="mt-6 font-heading text-2xl font-bold text-white">{title}</h3>
      <p data-testid={`${testId}-text`} className="mt-3 text-sm leading-7 text-slate-400">{text}</p>
    </>
  );
  
  const className = "group block h-full rounded-3xl border border-white/10 bg-[#1C1917]/45 p-7 backdrop-blur-xl transition-all duration-300 hover:-translate-y-2 hover:border-white/20 hover:bg-[#1C1917]/70";

  return to ? (
    <Link to={to} data-testid={testId} className={className}>
      {content}
    </Link>
  ) : (
    <div data-testid={testId} className={className}>
      {content}
    </div>
  );
};

export default function Home() {
  const [api, setApi] = useState(null);

  useEffect(() => {
    if (!api) {
      return;
    }
    const interval = setInterval(() => {
      api.scrollNext();
    }, 4000);

    return () => clearInterval(interval);
  }, [api]);

  useSEO({
    title: null,
    description: "Beginner-friendly surf, mellow waves, safe instructors, kite sessions, community beach days, and curated stays in Kannur, Kerala.",
    path: "/",
  });

  return (
    <main data-testid="home-page" className="overflow-hidden bg-[#0A0F1C] text-white">
      <section data-testid="home-hero-section" className="relative min-h-screen px-5 pt-32 md:px-10">
        <div className="absolute inset-0 opacity-45" style={{ backgroundImage: `url(${media.heroTexture})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,94,58,0.35),transparent_30%),linear-gradient(180deg,rgba(10,15,28,0.35),#0A0F1C_92%)]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="animate-rise">
            <p data-testid="home-hero-kicker" className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF5E3A]">Kerala’s coastal playground</p>
            <h1 data-testid="home-hero-title" className="mt-6 max-w-5xl font-heading text-5xl font-black leading-none tracking-tighter sm:text-6xl lg:text-7xl">
              Ride Kannur’s sunrise coast.
            </h1>
            <p data-testid="home-hero-description" className="mt-7 max-w-2xl text-base leading-8 text-slate-300 md:text-lg">
              Beginner-friendly surf, mellow waves, safe instructors, kite sessions, community beach days, and stays shaped around Kannur’s coast.
            </p>
            <div className="mt-10 grid grid-cols-[1fr_auto] gap-4 sm:flex sm:flex-row">
              <Button data-testid="home-hero-book-button" asChild className="col-span-2 h-12 rounded-xl bg-[#FF5E3A] px-8 font-semibold uppercase tracking-[0.16em] text-white shadow-[0_0_28px_rgba(255,94,58,0.22)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#FF7B5C] sm:col-span-1">
                <a href={contact.whatsappIntents.planSession} target="_blank" rel="noreferrer">Plan Your Session</a>
              </Button>
              <Button data-testid="home-hero-activities-button" asChild variant="outline" className="h-12 rounded-xl border-white/15 bg-white/5 px-8 font-semibold uppercase tracking-[0.16em] text-white backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/10 hover:text-white">
                <Link to="/activities">See Activities</Link>
              </Button>
              <Button data-testid="home-hero-instagram-button" asChild aria-label="Open Kannur Surf Club Instagram" className="h-12 w-12 rounded-xl border border-white/10 bg-white/10 p-0 text-white backdrop-blur transition-all duration-300 hover:border-white/25 hover:bg-white hover:text-[#0A0F1C]">
                <a href={contact.instagramUrl} target="_blank" rel="noreferrer" aria-label="Open Kannur Surf Club Instagram"><Instagram className="h-5 w-5" /></a>
              </Button>
            </div>
          </div>
          <div data-testid="home-hero-image-card" className="relative animate-rise-delay">
            <div className="absolute -left-8 -top-8 h-40 w-40 bg-[#D946EF]/25 blur-3xl" />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/5 p-3 shadow-2xl shadow-black/40 backdrop-blur-xl">
              <Carousel setApi={setApi} opts={{ loop: true }} className="w-full">
                <CarouselContent>
                  <CarouselItem>
                    <img data-testid="home-hero-surf-image-3" src={media.heroSurfJoti} alt="Surfer riding a wave at Kannur coast" decoding="async" className="aspect-[4/3] w-full rounded-[1.5rem] object-cover object-center sm:aspect-[5/4] lg:aspect-[4/4.2]" />
                  </CarouselItem>
                  <CarouselItem>
                    <img data-testid="home-hero-surf-image-2" src={media.heroSurfGroup} alt="Surfer at Kannur coast" decoding="async" className="aspect-[4/3] w-full rounded-[1.5rem] object-cover object-center sm:aspect-[5/4] lg:aspect-[4/4.2]" />
                  </CarouselItem>
                  <CarouselItem>
                    <img data-testid="home-hero-surf-image-1" src={media.heroSurf} alt="Surfer riding a wave at sunrise on the Kannur coast" fetchpriority="high" decoding="async" className="aspect-[4/3] w-full rounded-[1.5rem] object-cover object-center sm:aspect-[5/4] lg:aspect-[4/4.2]" />
                  </CarouselItem>
                </CarouselContent>
              </Carousel>
              <div data-testid="home-hero-stat-card" className="absolute bottom-7 left-7 right-7 rounded-2xl border border-white/15 bg-[#0A0F1C]/78 p-5 backdrop-blur-xl">
                <p className="whitespace-nowrap font-heading text-xl font-bold text-white sm:text-2xl">Surf • Stay • Gather</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ScrollVideoStage />

      <section data-testid="home-bento-section" className="px-5 pb-24 pt-12 md:px-10 md:pb-32 md:pt-16">
        <div className="mx-auto max-w-7xl">
          <div data-testid="home-bento-main" className="rounded-3xl border border-white/10 bg-[#1C1917]/50 p-8 backdrop-blur-xl lg:p-12">
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#FF5E3A]">What we do</p>
            <h2 data-testid="home-bento-title" className="mt-5 font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl">Ocean days without the rough edges.</h2>
            <p data-testid="home-bento-copy" className="mt-6 max-w-2xl text-base leading-8 text-slate-400">
              From first-light mellow surf to golden-hour wind sessions, every experience is grounded in local knowledge, honest guidance, and Kannur’s coastal rhythm.
            </p>
          </div>
          <div className="mt-8 grid gap-8 sm:grid-cols-2">
            <FeatureCard to="/activities" testId="home-feature-activities" icon={Waves} title="Water activities" text="Safe beginner surf, kite surf, kayaking, SUP rentals, and water confidence sessions." />
            <FeatureCard to="/accommodation" testId="home-feature-stays" icon={Hotel} title="Stay by the coast" text={`${stays.length} stay routes — a luxury high-rise apartment, Eva’s Homestay, and a surf hostel coming soon.`} />
            <FeatureCard to="/about" testId="home-feature-community" icon={Compass} title="Local community" text="Wave sessions, music nights, beach events, and gatherings shaped by the surfers of Kannur." />
            <FeatureCard to="/contact" testId="home-feature-contact" icon={MessageSquare} title="Get in touch" text="Reach out to plan your session, ask about conditions, or just say hello." />
          </div>
        </div>
      </section>

      <section data-testid="home-cta-section" className="px-5 pb-24 md:px-10 md:pb-32">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-white/10 bg-[linear-gradient(135deg,rgba(255,94,58,0.22),rgba(217,70,239,0.14),rgba(13,148,136,0.12))] p-8 backdrop-blur-xl md:p-14">
          <p data-testid="home-cta-kicker" className="text-xs font-bold uppercase tracking-[0.28em] text-[#FFC059]">Ready for the next tide?</p>
          <div className="mt-5 grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
            <h2 data-testid="home-cta-title" className="font-heading text-4xl font-black tracking-tight text-white sm:text-5xl">Follow the club for surf days, kite updates, community events, and stay announcements.</h2>
            <div className="flex flex-row gap-3 lg:justify-end">
              <Button data-testid="home-cta-instagram-button" asChild aria-label="Open Kannur Surf Club Instagram" className="h-12 w-12 shrink-0 rounded-xl border border-white/10 bg-white/10 p-0 text-white backdrop-blur transition-all duration-300 hover:border-white/25 hover:bg-white hover:text-[#0A0F1C]">
                <a href={contact.instagramUrl} target="_blank" rel="noreferrer" aria-label="Open Kannur Surf Club Instagram"><Instagram className="h-5 w-5" /></a>
              </Button>
              <Button data-testid="home-cta-contact-button" asChild className="h-12 w-fit rounded-xl bg-[#FF5E3A] px-8 font-semibold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-[#FF7B5C]">
                <Link to="/contact">Contact the Club</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
